import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { tap, delay, map, switchMap, shareReplay, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { LocalEnum } from  '../enums/localstorage';
import * as moment from 'moment';
import { ProfilePicture } from '../users/user-image';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, ActivatedRoute } from '@angular/router';
import { FunctionService } from './functions.service';
import { PlannexeAppService } from '@plannexe/shared/services/plannexe-app.service';
import { IDENEDI_CODE_STORAGE_KEY, AuthService as AuthServiceMain, AuthType } from 'src/app/shared/services/auth.service';
import { RequestCacheService } from './request.cache.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  localUser: any;
  lastRefreshIdenediTokenTime = new BehaviorSubject<any>(new Date().getTime());

  constructor(
    private http: HttpClient,
    private backend: HttpBackend,
    private profilePicture: ProfilePicture,
    private router: Router,
    private functionService: FunctionService,
    private plannexeAppService: PlannexeAppService,
    private stemexeAuthService: AuthServiceMain,
    private cacheService: RequestCacheService,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
  ) {
    const user = this.user;
    if(this.isLoggedIn && moment().diff(moment(user.time || 100), 'hours', true) > 24){
      this.refreshIdenediToken();
    }
  }

  get isLoggedIn(): boolean{
    const local = localStorage.getItem(LocalEnum.isloggedin);
    const tempToken = localStorage.getItem(LocalEnum.tempToken);
    return (local==='true' || tempToken) ? true : false;
  }
  set isLoggedIn(loggedin: boolean){
    localStorage.setItem(LocalEnum.isloggedin, loggedin.toString());
  }

  get token(): string | null{
    let token = localStorage.getItem(LocalEnum.token);
    return token ? token : localStorage.getItem(LocalEnum.tempToken);
  }
  set token(r: string | null){
    localStorage.setItem(LocalEnum.token, r ? r : '');
  }
  get FileServerToken(): string | null{
    return localStorage.getItem(LocalEnum.fileToken);
  }
  set FileServerToken(r: string | null){
    localStorage.setItem(LocalEnum.fileToken, r ? r : '');
  }

  get userId(): number{
    const user = this.user;
    return user ? parseInt(user.userId, 10) : 0;
  }
  get isAdmin(): boolean{
    const user = this.user;
    return user ? user.userIsAdmin : false;
  }
  get isPM(): boolean{
    const user = this.user;
    return user ? user.userIsPM : false;
  }
  get organizationId(): number{
    const user = this.user;
    return user ? parseInt(user.organizationId, 10) : 0;
  }
  get organizationName(): string{
    const user = this.user;
    let name = '';
    if(user.organizationName){
      name = user.organizationName;
    }
    else if(user && user.organizations.length > 0){
      name = user.organizations[0].name;
    }
    return name;
  }
  get userName(): string{
    const user = this.user;
    return user ? (user.firstName + ' ' + user.lastName) : '';
  }
  get userPicture(): string{
    const user = this.user;
    if(user.profilePicture){
      return user.profilePicture;
    }
    return this.profilePicture.generate(user.firstName, user.lastName);
  }
  get idenediToken(): string{
    const user = this.user;
    return user ? user.idenedi_AccessToken : '';
  }
  get idenediRefreshToken(): string{
    const user = this.user;
    return user ? user.refresh_token : '';
  }
  get idenediKey(): string{
    const user = this.user;
    return user ? user.idenediKey : '';
  }
  get idenediGroup(): string{
    const user = this.user;
    return user ? user.linkedGroupId : '';
  }
  get user(): any{
    let user: any = localStorage.getItem(LocalEnum.user);
    if (user !== null && user !== undefined && user !== 'undefined'){
      user = JSON.parse(user);
    }
    return user ? user : {};
  }
  get fileTokenHeader(): any{
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.FileServerToken
      })
    }
  }
  get plannexeTokenHeader(): any{
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + this.token
      })
    }
  }
  get loggedinVersion(): string{
    let _v: any = localStorage.getItem(LocalEnum.version);
    return _v ? _v : '1.0.0';
  }
  isFeatureEnabled(featureName: any, moduleName: any): boolean{
    let result = true;
    const features = this.user.organizationFeatures;
    if(features){
      const feature = features.find((f: any) => f.featureName === featureName);
      if(feature){
        feature.modules = this.functionService.safeJsonArray(feature.modulesJson);
        const module = feature.modules.find((m: any) => m.moduleName === moduleName);
        result = module ? module.isEnabled : result;
      }
    }
    return result;
  }

  getFileToken(): any {
    const data = {
      api_key: environment.plannexe.file.apiKey,
      client_id: environment.plannexe.file.clientID,
      client_secret: ""
    };
    // tap => token store; shareReplay(1) => ek hi HTTP call, chahe internal subscribe ho ya caller (jaise
    // interceptor ka 401 retry) isay dobara subscribe kare. Warna do POST /api/token requests jaate the.
    const httpPost = this.http.post(environment.plannexe.file.api + '/api/token', data).pipe(
      tap((token: any) => {
        localStorage.setItem(LocalEnum.fileToken, token.token);
      }),
      shareReplay(1)
    );
    httpPost.subscribe();
    return httpPost;
  }

  login(): Observable<boolean> {
    return of(true).pipe(
      delay(1000),
      tap(val => this.isLoggedIn = true)
    );
  }

  logout(): void {
    // Saare auth-related keys clear karo, warna stale tempToken/user/idenedi tokens reh jaate hain
    // aur re-login par overlap hota hai (testing ke dauraan localStorage manually clear karna padta tha).
    localStorage.removeItem(LocalEnum.token);
    localStorage.removeItem(LocalEnum.isloggedin);
    localStorage.removeItem(LocalEnum.tempToken);
    localStorage.removeItem(LocalEnum.fileToken);
    localStorage.removeItem(LocalEnum.user);
    localStorage.removeItem(LocalEnum.ssOrganization);
    localStorage.removeItem(LocalEnum.espOrganization);
    localStorage.removeItem(IDENEDI_CODE_STORAGE_KEY);
    localStorage.removeItem('idenedi.auth.token');
    localStorage.removeItem('idenedi.auth.refresh-token');
    // localStorage.removeItem('stemexe.idenedi.auth.refresh-token');
    localStorage.removeItem('SignInByIdenediToken');
    sessionStorage.removeItem('esp.login.data');
    this.cacheService.removeAllCache();
    this.isLoggedIn = false;

    
  }

  
  getSSOrganization(): any {
    const httpPost = this.http.get(environment.plannexe.idenedi.api + '/api/group/'+this.idenediGroup+'/addon');
    httpPost.subscribe((addons: any) => {
      const ss = addons.find((s: any) => s.Type === 'Simple Strata');
      const esp = addons.find((s: any) => s.Type === 'ESP');
      localStorage.setItem(LocalEnum.ssOrganization, ss ? ss.PublicProperties.organziationId : 0);
      localStorage.setItem(LocalEnum.espOrganization, esp ? esp.PublicProperties.organziationId : 0);
    })
    return httpPost;
  }
  
  refreshIdenediToken(): any {
    if(this.lastRefreshIdenediTokenTime.getValue() > new Date().getTime() - 1000 * 60){
      return;
    }
    this.lastRefreshIdenediTokenTime.next(new Date().getTime());
    const newHttpClient = new HttpClient(this.backend);
    const header = new HttpHeaders({"Authorization":  'Bearer ' + this.token})
    const httpPost = newHttpClient.post(this.plannexeAppService.plannexeBaseUrl + '/api/Idenedi/GetUserInfo?UserId='+ this.userId, {}, {headers: header});
    httpPost.subscribe((token: any) => {
      let user = this.user;
      user.time = new Date().getTime();
      user.idenedi_AccessToken = token.result.accessToken;
      user.refresh_token = token.result.refreshToken;

      localStorage.setItem(LocalEnum.user, JSON.stringify(user));
      localStorage.setItem('idenedi.auth.refresh-token', token.result.refreshToken);
      localStorage.setItem('idenedi.auth.token', token.result.accessToken);
    });
    return httpPost;
  }
  syncFeatures(): void {
    if(this.isLoggedIn){
      this.http.post(this.plannexeAppService.plannexeBaseUrl + '/api/OrganizationFeatures/GetAllOrgFeaturesShort', {
        pageNum: 0, pageSize: 10000, search: "", enabledFeaturesOnly: false
      }, this.plannexeTokenHeader).pipe(shareReplay()).subscribe((features: any) => {
        let user: any = localStorage.getItem(LocalEnum.user);
        if (user !== null && user !== undefined && user !== 'undefined'){
          user = JSON.parse(user);
        }
        if(user){
          user.organizationFeatures = features.result;
          localStorage.setItem(LocalEnum.user, JSON.stringify(user));
        }
      });
    }
  }
  
  plannexeAddon(): Observable<any>{
    return this.stemexeAuthService
    .getAddonsAuth(this.stemexeAuthService.idenediGroupId)
    .pipe(
      map(addons => {
        return addons.find(
          addon => addon.Type === AuthType.plannexe
        );
      })
    );
  }
  plannexeToken(): Observable<any> {
    const queryparams = this.activatedRoute.snapshot.queryParams;
    const REFRESH_TOKEN_STORAGE_KEY = "idenedi.auth.refresh-token";
    const STEMEXE_REFRESH_TOKEN_STORAGE_KEY = "stemexe.idenedi.auth.refresh-token";
    return this.plannexeAddon().pipe(
      switchMap(addon => {
        if (!addon) {
          return of(null);
        }
        if(addon && addon.BaseUrl){
          this.plannexeAppService.setBaseUrl(addon.BaseUrl);
        }
        return forkJoin([this.plannexeAddon()]).pipe(
          switchMap(results => {
            const code = localStorage.getItem(IDENEDI_CODE_STORAGE_KEY);
            const refreshToken = localStorage.getItem(STEMEXE_REFRESH_TOKEN_STORAGE_KEY ?? REFRESH_TOKEN_STORAGE_KEY);
            const data = {
              code,
              redirectUri: environment.plannexe.idenedi.redirect,
              deviceRegisterId: "",
              deviceTypeId: "",
              LinkedGroupId: results[0].PublicProperties.groupIdenedi,

              inviteCode: queryparams.invitecode,
              access_token: this.stemexeAuthService.idenediToken,
              refresh_token: refreshToken,
              idenedi: this.stemexeAuthService.getUserIdenedi()
            };
            return this.http
              .post<any>(results[0].BaseUrl + "/api/Idenedi/AuthenticateV2Web", data)
              .pipe(
                map(user => {
                  localStorage.setItem(LocalEnum.isloggedin, "true");
                  localStorage.setItem(LocalEnum.token, user.result.access_token);
                  localStorage.setItem(LocalEnum.user, JSON.stringify(user.result));
                  this.getFileToken();
                  return results[0];
                })
              );
          })
        );
      })
    );
  }
  stemexeTabs(): Array<any> {
    let tabs:Array<any> = [
      {
        name: this.translate.instant('projects'),
        link: 'projects'
      },
      {
        name: this.translate.instant('my_space'),
        link: 'myspace'
      },
      {
        name: this.translate.instant('support'),
        link: 'support'
      },
      {
        name: this.translate.instant('boards'),
        link: 'boards'
      },
    ];
    if(this.isAdmin){
      tabs.push({
        name: this.translate.instant('settings'),
        link: 'settings'
      });
    }
    return tabs;
  }
  stemexeTabClick(event: string): void {
    let link = event=='myspace' ? '/myspace' : 
        (event=='settings' ? '/settings' : 
          (event=='support' ? '/support' : 
            (event=='boards' ? '/boards' : '/project/list')));
    this.router.navigate([environment.plannexe.moduleLink + link]);
  }
}



@Injectable({
  providedIn: "root"
})
export class PlannexeResolverService  {
  constructor(
    private authService: AuthServiceMain,
    private plannexeAuthService: AuthService,
    private plannexeAppService: PlannexeAppService,
  ) {}
  resolve(
    // route: ActivatedRouteSnapshot,
    // state: RouterStateSnapshot
  ) {
    if(this.plannexeAuthService.isLoggedIn){
      return this.plannexeAddon().pipe(
        switchMap(addon => {
          if(addon && addon.BaseUrl){
            this.plannexeAppService.setBaseUrl(addon.BaseUrl);
          }
          return of(addon);
        })
      );
    }
    // Not logged in (e.g. custom domain before login): no Idenedi token, the code API would 401 and block navigation
    if (!this.authService.isLoggedIn()) {
      return of(null);
    }
    return this.authService
      .getIdenediAuthorizationCodeForClient(environment.engagementClientId)
      .pipe(
        switchMap((code) => {
          localStorage.setItem(IDENEDI_CODE_STORAGE_KEY, code);
          return this.plannexeAuthService.plannexeToken();
        }),
        catchError(() => of(null))
      );
  }
  plannexeAddon(): Observable<any>{
    return this.authService
    .getAddonsAuth(this.authService.idenediGroupId)
    .pipe(
      map(addons => {
        return addons.find(
          addon => addon.Type === AuthType.plannexe
        );
      })
    );
  }
}
